'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

const founders = [
    {
        name: "Abdul Sohail Samad",
        role: "Founder & Lead Developer",
        image: "/abdulsohailsamad.jpg",
        bio: "Expert Full-Stack Developer architecting robust, scalable digital infrastructures.",
        socials: { linkedin: "#", twitter: "#" }
    }
];

const FoundersSection = () => {
    const containerRef = useRef<HTMLDivElement>(null);

    return (
        <section ref={containerRef} className="relative py-32 bg-slate-50 overflow-hidden">

            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <div className="grid grid-cols-1 gap-20 max-w-2xl mx-auto">

                    {/* Header Block */}
                    <div className="lg:col-span-2 text-center mb-10">
                        <span className="text-sm font-bold text-indigo-600 tracking-[0.2em] uppercase block mb-4">The Visionaries</span>
                        <h2 className="text-5xl lg:text-7xl font-black text-slate-900 tracking-tight">
                            Meet the <span className="italic font-serif text-indigo-600">Architects</span>
                        </h2>
                    </div>

                    {founders.map((founder, idx) => (
                        <div key={idx} className="group relative">
                            {/* Image Container - The "Lens" */}
                            <div className="relative aspect-[3/4] overflow-hidden rounded-[2rem] shadow-2xl transition-all duration-700 hover:shadow-indigo-500/30">
                                {/* Image */}
                                <Image
                                    src={founder.image}
                                    alt={founder.name}
                                    fill
                                    className="object-cover transition-transform duration-1000 group-hover:scale-110 grayscale group-hover:grayscale-0"
                                />

                                {/* Overlay Gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-80 group-hover:opacity-60 transition-opacity duration-500" />

                                {/* Content Overlay (always visible but transforms) */}
                                <div className="absolute bottom-0 left-0 w-full p-8 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                    <h3 className="text-3xl font-bold text-white mb-2">{founder.name}</h3>
                                    <p className="text-indigo-300 font-mono text-sm uppercase tracking-wider mb-4">{founder.role}</p>

                                    <div className="h-0 group-hover:h-auto overflow-hidden transition-all duration-500 origin-bottom">
                                        <p className="text-slate-300 text-lg leading-relaxed mb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100">
                                            {founder.bio}
                                        </p>

                                        <a
                                            href="https://www.instagram.com/abdulsohailsamad"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-6 inline-flex items-center gap-2 text-white border-b border-white/50 pb-1 hover:text-indigo-300 hover:border-indigo-300 transition-all duration-300 group/btn"
                                        >
                                            <span>View Profile</span>
                                            <Icon name="ArrowRightIcon" className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FoundersSection;